import type { HTMLAttributes } from 'react'
import { cn } from '../lib/cn'

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Announced to assistive tech while a region loads; omit inside an already-busy region. */
  label?: string
}

/**
 * F0-19 — loading placeholder.
 *
 * NFR-PE-002 is about *perceived* latency: a skeleton that mirrors the eventual
 * layout beats a spinner, because the page does not reflow when data lands.
 */
export function Skeleton({ className, label, ...rest }: SkeletonProps) {
  return (
    <div
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-live={label ? 'polite' : undefined}
      className={cn('animate-pulse rounded-md bg-surface-inset', className)}
      {...rest}
    />
  )
}

/** Text-block skeleton with a short last line, which reads as prose rather than a bar. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={cn('h-4', i === lines - 1 && 'w-2/3')} />
      ))}
    </div>
  )
}
